import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CompanyDetailsResponse } from '@/models/payment';
import {
    SubTitle,
    SMRegular,
    TextSMRegular,
    SMMedium700,
    SMSemiBold,
    TextMedium
} from '@/typography/Typography';
import { DangerButton, PrimaryButton } from '@/components/shared/Button';
import { NumericInput } from '@/components/shared/NumericInput';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useMasterCardConnectURL } from '@/services/paymentsQuery';
import Chip from '@/components/shared/Chip';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import MasterCardLogo from '@/icons/master_card.svg';

export const AutoTopUpSettings = ({
    currentCompany,
    topUpAmount,
    setTopUpAmount,
    onSaveAndConnect,
    onFormatError,
    resetKey
}: {
    currentCompany?: CompanyDetailsResponse;
    isAutoTopupEnabled: boolean;
    setIsAutoTopupEnabled: (value: boolean) => void;
    topUpAmount: number;
    setTopUpAmount: (value: number) => void;
    onSaveAndConnect?: () => Promise<boolean>;
    onFormatError?: (hasError: boolean) => void;
    resetKey?: number;
}) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();

    const isConnected = currentCompany?.autoTopupStatus === 'Connected';

    // Prefetch MasterCard connect URL using React Query
    const { data: masterCardConnectData } = useMasterCardConnectURL(
        currentCompany?.paymentProvider === 'None' &&
            (currentCompany?.paymentSource === 'InstaPaidAccount' ||
                currentCompany?.paymentSource === 'InstaPaidEurAccount')
    );

    const handleConnectNow = async () => {
        try {
            // First validate and save if the callback is provided
            if (onSaveAndConnect) {
                const isSaved = await onSaveAndConnect();
                if (!isSaved) {
                    // Validation failed or save failed, don't open the link
                    return;
                }
            }

            // Then open the MasterCard connection link
            if (masterCardConnectData?.data?.connectUrl) {
                window.open(masterCardConnectData.data.connectUrl, '_blank');
                setAlert(t('opening_mastercard_connection'), 'info');
            } else {
                setAlert(t('failed_get_connection_url'), 'error');
            }
        } catch (error) {
            console.error('Error connecting to MasterCard:', error);
            setAlert(t('failed_connect_mastercard'), 'error');
        }
    };

    const handlePause = async () => {
        await updateCompanyDetails.mutateAsync({
            autoTopupEnabled: false
        });
    };

    const { updateCompanyDetails } = usePaymentsMutations();

    const handleActivate = async () => {
        await updateCompanyDetails.mutateAsync({
            autoTopupEnabled: true
        });
    };

    return (
        <>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <SMMedium700>{t('auto_topup')}</SMMedium700>
                        <SubTitle color={'#667085'}>
                            {t('auto_top_up_description')}
                        </SubTitle>
                    </Box>
                </Box>

                <Box>
                    <NumericInput
                        data-testid="amount-to-topup-input"
                        digits={0}
                        resetKey={resetKey}
                        value={topUpAmount}
                        onChange={(val) => {
                            setTopUpAmount(val ?? 0);
                        }}
                        onFormatError={onFormatError}
                        label={t('amount_to_topup', {
                            amount: currentCompany?.lowBalanceNotificationThreshold,
                            currency: currentCompany?.currency || 'DKK'
                        })}
                        inputAdornment={
                            <SMRegular
                                sx={{
                                    color: '#667085'
                                }}
                            >
                                {currentCompany?.currency || 'DKK'}
                            </SMRegular>
                        }
                    />
                </Box>

                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 3,
                            width: '100%',
                            padding: '16px',
                            border: '1px solid #E4E7EC',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <img
                                src={MasterCardLogo}
                                alt="MasterCard"
                                width="40"
                                height="40"
                            />
                            <Box>
                                <TextSMRegular
                                    fontWeight={600}
                                    sx={{ color: '#101828' }}
                                >
                                    {t('mastercard_vendor_service')}
                                </TextSMRegular>
                                <SMRegular sx={{ color: '#667085' }}>
                                    {t('monthly_fee_amount', {
                                        amount:
                                            currentCompany?.monthlyAutoTopUpFee ??
                                            '<Not set>',
                                        currency:
                                            currentCompany?.currency || 'DKK'
                                    })}
                                </SMRegular>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <TextMedium>{t('customer_id')}</TextMedium>

                                {isConnected ? (
                                    <>
                                        {currentCompany?.autoTopupEnabled && (
                                            <Chip
                                                label={
                                                    <SMSemiBold>
                                                        {
                                                            currentCompany?.customerId
                                                        }{' '}
                                                        {t('connected')}
                                                    </SMSemiBold>
                                                }
                                                color="success"
                                            />
                                        )}
                                        {!currentCompany?.autoTopupEnabled && (
                                            <Chip
                                                label={
                                                    <SMSemiBold>
                                                        {
                                                            currentCompany?.customerId
                                                        }{' '}
                                                        {t('paused')}
                                                    </SMSemiBold>
                                                }
                                                color="warning"
                                            />
                                        )}
                                    </>
                                ) : (
                                    <Chip
                                        label={
                                            <SMSemiBold>
                                                {currentCompany?.customerId ??
                                                    t('not_set')}
                                            </SMSemiBold>
                                        }
                                    />
                                )}
                            </Box>

                            {isConnected ? (
                                currentCompany?.autoTopupEnabled ? (
                                    <DangerButton
                                        size="small"
                                        onClick={handlePause}
                                    >
                                        {t('pause')}
                                    </DangerButton>
                                ) : (
                                    <PrimaryButton
                                        size="small"
                                        onClick={handleActivate}
                                    >
                                        {t('activate')}
                                    </PrimaryButton>
                                )
                            ) : (
                                <PrimaryButton
                                    size="small"
                                    onClick={handleConnectNow}
                                >
                                    {t('connect_new_window')}
                                </PrimaryButton>
                            )}
                        </Box>
                    </Box>
                    <SubTitle
                        maxWidth={'80%'}
                        mt={2}
                        mb={3}
                        sx={{ color: '#667085' }}
                    >
                        {t('activate_auto_topup_with_customer_id')}
                    </SubTitle>
                </>
            </Box>
        </>
    );
};
